import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Form,
  FormInstance,
  AutoComplete,
  Select,
  Input,
  Row,
  Col,
  notification,
  Button,
} from "antd";
import { OptionData, OptionGroupData } from "rc-select/lib/interface";
import styles from "../MemberProfile.module.scss";
import textStyles from "../../../Shared/Styles/TextStyles.module.scss";
import {
  getAutoCompleteAddress,
  getPlaceDetails,
  getWandaServiceArea,
  validateUspsAddress,
} from "../../../../services/LocationService";
import {
  Address,
  AddressRef,
  AutoCompleteOption,
  defaultSelectedAddress,
} from "../../../../types/LocationTypes";
import InvalidAddressModal from "../../../CreatePerson/Modals/InvalidAddressModal";
import { useServiceUserCreationCtx } from "../../../../context/ServiceUserCreationContext";
import {
  constructAddressByPlaceDetails,
  validateAddressObject,
} from "../../../../helpers/personHelpers";
import {
  useGroceryCtx,
} from "../../../../context/GroceryContext";

type Props = {
  addressType: "homeAddress" | "mailingAddress" | "deliveryAddress";
  form: FormInstance;
  validateAndCheckForm: Function;
  shouldValidateZip: boolean;
  isCaregiverProfile: boolean;
};

const ProfileAddress = forwardRef<AddressRef, Props>((props, ref) => {
  const {
    form,
    addressType,
    validateAndCheckForm,
    shouldValidateZip,
    isCaregiverProfile,
  } = props;

  const [isEnterManualAddress, setIsEnterManualAddress] = useState<boolean>(
    false
  );
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<
    AutoCompleteOption[]
  >([]);
  const [selectedAddress, setSelectedAddress] = useState<Address>(
    defaultSelectedAddress
  );
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [
    isInvalidAddressModalVisible,
    setIsInvalidAddressModalVisible,
  ] = useState<boolean>(false);
  const [isUspsAddressError, setIsUspsAddressError] = useState<boolean>(false);

  useImperativeHandle(ref, () => ({
    handleResetAddressLookup,
    validateManualAddress,
    isEnterManualAddress,
  }));

  const { statesCtx, getStateList } = useServiceUserCreationCtx();
  const [states] = statesCtx;

  const { deliveryAddressCtx } = useGroceryCtx();
  const [, setDeliveryAddress] = deliveryAddressCtx;

  useEffect(() => {
    !states.length && getStateList();
  }, []);

  useEffect(() => {
    const isInitialAddressAvailable = form.getFieldValue(addressType)?.address1;
    const isSameAsHomeAddress = form.getFieldValue("isSameAsHomeAddress");
    const isHomeAddress = addressType === "homeAddress";

    if (isInitialAddressAvailable && (isHomeAddress || !isSameAsHomeAddress)) {
      (async () => {
        try {
          const { zip } = form.getFieldValue(addressType);
          await checkZipUnderServiceArea(zip);
        } catch (error) {
          notification.error({
            message: "Zip Validation Failed.",
          });
        }
      })();
    }
  }, []);

  async function validateManualAddress() {
    if (!isEnterManualAddress) {
      return { isValid: true, uspsAddress: null };
    }

    try {
      const uspsAddress = await validateUspsAddress(
        form.getFieldValue(addressType)
      );

      if (uspsAddress) {
        return { isValid: true, uspsAddress };
      }

      setIsUspsAddressError(true);
      return { isValid: false, uspsAddress: null };
    } catch (error) {
      setIsUspsAddressError(true);
      return { isValid: false, uspsAddress: null };
    }
  }

  async function checkZipUnderServiceArea(zip: string) {
    try {
      if (shouldValidateZip) {
        const serviceAreas = await getWandaServiceArea(zip);
        addressType === "deliveryAddress" &&
          setDeliveryAddress((prev: Address) => ({
            ...prev,
            organizationId: serviceAreas[0]?.organizationId,
          }));
        if (!serviceAreas.length) {
          setIsInvalidAddressModalVisible(true);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  function handleCantFindAddress() {
    setIsDisabled(false);
    setIsEnterManualAddress(true);
  }

  function handleResetAddressLookup() {
    const resetAddress = { ...defaultSelectedAddress, state: undefined };

    if (form.getFieldValue("isSameAsHomeAddress")) {
      form.setFieldsValue({
        homeAddress: resetAddress,
        mailingAddress: resetAddress,
      });
    } else {
      form.setFieldsValue({
        [addressType]: resetAddress,
      });
    }
    setIsUspsAddressError(false);
    setSelectedAddress(defaultSelectedAddress);
    setIsEnterManualAddress(false);
    setIsDisabled(true);
    setAutoCompleteOptions([]);
    validateAndCheckForm();
  }

  function handleDoesntLookQuiteRight() {
    setIsDisabled(false);
    setIsEnterManualAddress(true);
  }

  async function handleAddressSearch(searchText: string) {
    try {
      const data = await getAutoCompleteAddress(searchText);
      if (data && Array.isArray(data.predictions)) {
        const descriptions = data.predictions.map(
          (item: { description: string; place_id: string }) => ({
            value: item.place_id,
            label: item.description,
          })
        );
        setAutoCompleteOptions(descriptions);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to get auto complete address.",
      });
    }
  }

  async function handleAddressSelect(
    value: string,
    option: OptionData | OptionGroupData,
  ) {
    try {
      let tempAddressDetails: Address = { ...defaultSelectedAddress };
      const description =
        typeof option.label === "string" ? option.label : undefined;
      const placeDetails = await getPlaceDetails(option.value, description);
      if (placeDetails.results) {
        const addressComponents = placeDetails.results[0].address_components;

        const { addressDetails } = constructAddressByPlaceDetails(
          tempAddressDetails,
          addressComponents,
        );

        tempAddressDetails = addressDetails;
      }

      setSelectedAddress(tempAddressDetails);

      form.setFieldsValue({
        [addressType]: tempAddressDetails,
      });

      if (form.getFieldValue("isSameAsHomeAddress")) {
        form.setFieldsValue({
          mailingAddress: tempAddressDetails,
        });
      }

      await checkZipUnderServiceArea(tempAddressDetails.zip);
    } catch (error) {
      notification.error({
        message: "Select Address Failed.",
      });
    } finally {
      validateAndCheckForm();
    }
  }

  async function handleAddressChange(fieldName: string) {
    try {
      isUspsAddressError && setIsUspsAddressError(false);

      if (form.getFieldValue("isSameAsHomeAddress")) {
        const address = form.getFieldValue("homeAddress");
        if (validateAddressObject(address)) {
          form.setFieldsValue({
            mailingAddress: address,
          });
          validateAndCheckForm();
        }
      }

      if (fieldName === "zip") {
        const { zip } = form.getFieldValue(addressType);

        if (zip?.length === 5) {
          await checkZipUnderServiceArea(zip);
        }
      }
    } catch (error) {
      notification.error({
        message: "Failed Address Check",
      });
    }
  }

  function handleAutoCompleteChange() {
    const fullAddress = form.getFieldValue(addressType)?.fullAddress;
    if (!fullAddress) {
      form.setFieldsValue({
        [addressType]: defaultSelectedAddress,
      });
      validateAndCheckForm();
    }
  }

  return (
    <>
      {!isEnterManualAddress && !selectedAddress.address1 ? (
        <>
          <Form.Item
            name={[addressType, "fullAddress"]}
            className={styles.marginDown4}
          >
            <AutoComplete
              options={autoCompleteOptions}
              onSearch={handleAddressSearch}
              onSelect={handleAddressSelect}
              onChange={handleAutoCompleteChange}
              placeholder="Enter an address"
              className={styles.autoCompleteInput}
            />
          </Form.Item>
          <Row gutter={[0, 25]}>
            <Button
              type="link"
              onClick={handleCantFindAddress}
              className={styles.addressLinkBtn}
            >
              Can't find {isCaregiverProfile ? "your" : "their"} address?
            </Button>
            <Col span={24} />
          </Row>
        </>
      ) : (
        <>
          <Form.Item
            name={[addressType, "address1"]}
            className={`${(!isEnterManualAddress || selectedAddress.address1) &&
              styles.marginDown0}`}
          >
            <Input
              placeholder="Street address"
              value={selectedAddress.address1}
              className={`${styles.input} ${isDisabled &&
                styles.disabledInput} ${isUspsAddressError &&
                styles.errorBorder}`}
              disabled={isDisabled}
              onChange={() => handleAddressChange("address1")}
            />
          </Form.Item>
          {selectedAddress.address1 && (
            <Button
              type="link"
              onClick={handleDoesntLookQuiteRight}
              className={`${styles.addressLinkBtn} ${styles.marginVertical4}`}
            >
              Doesn't look quite right?
            </Button>
          )}

          <Form.Item name={[addressType, "address2"]}>
            <Input
              placeholder="Suite #, Apt #, PO Box, etc"
              className={`${styles.input} ${isUspsAddressError &&
                styles.errorBorder}`}
              onChange={() => handleAddressChange("address2")}
            />
          </Form.Item>

          <Row gutter={[10, 0]}>
            <Col span={12}>
              <Form.Item
                name={[addressType, "city"]}
                className={styles.marginDown0}
              >
                <Input
                  placeholder="City"
                  className={`${styles.cityInput} ${isDisabled &&
                    styles.disabledInput} ${isUspsAddressError &&
                    styles.errorBorder}`}
                  value={selectedAddress.city}
                  disabled={isDisabled}
                  onChange={() => handleAddressChange("city")}
                />
              </Form.Item>
            </Col>

            <Col span={5}>
              <Form.Item
                name={[addressType, "state"]}
                className={styles.marginDown0}
              >
                <Select
                  className={`${styles.stateSelect} ${isDisabled &&
                    styles.disabledSelectInput} ${isUspsAddressError &&
                    styles.errorSelectBorder}`}
                  placeholder="State"
                  options={states}
                  disabled={isDisabled}
                  suffixIcon=""
                  onChange={() => handleAddressChange("state")}
                />
              </Form.Item>
            </Col>

            <Col span={7}>
              <Form.Item
                name={[addressType, "zip"]}
                className={styles.marginDown0}
                rules={[
                  {
                    min: 5,
                    message: "",
                  },
                ]}
              >
                <Input
                  maxLength={5}
                  placeholder="Zip code"
                  className={`${styles.zipInput} ${isDisabled &&
                    styles.disabledInput} ${isUspsAddressError &&
                    styles.errorBorder}`}
                  value={selectedAddress.zip}
                  disabled={isDisabled}
                  onChange={() => handleAddressChange("zip")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Col span={24} className={styles.marginUp4Down24}>
            <Button
              type="link"
              onClick={handleResetAddressLookup}
              className={styles.addressLinkBtn}
            >
              Reset address lookup
            </Button>
            {isUspsAddressError && (
              <Row
                className={`${textStyles.errorNormalText14} ${styles.marginUp10}`}
              >
                Invalid Address
              </Row>
            )}
          </Col>
        </>
      )}

      <InvalidAddressModal
        form={form}
        addressType={addressType}
        visible={isInvalidAddressModalVisible}
        setVisibility={setIsInvalidAddressModalVisible}
        handleResetAddressLookup={handleResetAddressLookup}
      />
    </>
  );
});

export default ProfileAddress;
